import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { APP_NAME } from "../constants/expiry";

type State = "verifying" | "success" | "error";

const VerifyMagicLink: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { verifyMagicLink, isAuthenticated } = useAuth();

  const [state, setState] = useState<State>("verifying");
  const [message, setMessage] = useState("");
  const hasVerified = useRef(false); // StrictMode guard — prevent double invocation

  const token = params.get("token");

  // Auto-fire verification on mount
  useEffect(() => {
    if (!token) {
      if (isAuthenticated) {
        navigate("/items", { replace: true });
      } else {
        setState("error");
        setMessage(
          "No verification token found in this link. Please request a new sign-in link."
        );
      }
      return;
    }

    // Guard: never call twice (StrictMode fires effects twice in dev)
    if (hasVerified.current) return;
    hasVerified.current = true;

    verifyMagicLink(token)
      .then(() => {
        setState("success");
        const pendingRedirect = localStorage.getItem("post_login_redirect");
        if (pendingRedirect) localStorage.removeItem("post_login_redirect");
        const destination = pendingRedirect || "/items";
        setTimeout(() => navigate(destination, { replace: true }), 1200);
      })
      .catch((err: unknown) => {
        hasVerified.current = false;
        setState("error");
        const apiError = (err as { response?: { data?: { error?: string } } })
          ?.response?.data?.error;
        setMessage(
          apiError ||
            "This link is invalid or has already been used. Please request a new one."
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]" />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6] px-4">
      <div className="max-w-md w-full text-center space-y-6">

        {/* ── Verifying ─────────────────────────────────────────────── */}
        {state === "verifying" && (
          <>
            <div className="flex justify-center">
              <div className="h-16 w-16 bg-[#EFF6FF] rounded-full flex items-center justify-center">
                <svg
                  className="animate-spin h-8 w-8 text-[#2563EB]"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#111827]">
                Signing you in…
              </h2>
              <p className="mt-2 text-sm text-[#6B7280]">
                Verifying your {APP_NAME} sign-in link
              </p>
            </div>
          </>
        )}

        {/* ── Success ───────────────────────────────────────────────── */}
        {state === "success" && (
          <>
            <div className="flex justify-center">
              <div className="h-16 w-16 bg-[#DCFCE7] rounded-full flex items-center justify-center">
                <svg
                  className="h-8 w-8 text-[#16A34A]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#111827]">You're in!</h2>
              <p className="mt-2 text-sm text-[#6B7280]">
                Redirecting to your dashboard…
              </p>
            </div>
          </>
        )}

        {/* ── Error ─────────────────────────────────────────────────── */}
        {state === "error" && (
          <>
            <div className="flex justify-center">
              <div className="h-16 w-16 bg-[#FEF2F2] rounded-full flex items-center justify-center">
                <svg
                  className="h-8 w-8 text-[#DC2626]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#111827]">
                Link expired or already used
              </h2>
              <p className="mt-2 text-sm text-[#6B7280]">{message}</p>
            </div>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mx-auto block py-3 px-8 bg-[#2563EB] hover:bg-[#1D4ED8]
                         text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Request a new link
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyMagicLink;
